/**
 * Transport-aware wrapper for the `AddressesApi`: the single-address DETAIL read
 * (`getDetail`) plus the IDEMPOTENT overlay + membership operations (assignment
 * attach/detach + list, primary set/clear, entity-mapping get/put/delete).
 *
 * Wraps the REST `AddressesApi` so each of those ops takes the fast GraphQL path
 * when the central transport registry says so, and TRANSPARENTLY falls back to
 * REST on any GraphQL error — flipping a flag on can never harden into an outage.
 * Everything else on the api (create/update/remove, share, the cross-chain lens
 * `getByAddress`, the paginated `list`, wallet enrichment) is delegated
 * untouched: NON-idempotent writes and the richer catalog-block-per-row reads
 * stay on REST.
 *
 * `getDetail` is a pure read that resolves through the SAME commons
 * `address_read_facade` the REST detail route uses (one brain, two mouths), so
 * the row + `saved` + `other_scopes` (the row-tier scope switcher) cannot drift.
 * The write ops are routed because they are all idempotent on the backend
 * (deterministic overlay key / single-primary pointer / fixed-window mapping
 * upsert), which is exactly what makes a GraphQL-first-with-REST-fallback safe:
 * a retried or duplicated write converges to the same row.
 */
import type {
  AddressAssignment,
  AddressEntityMapping,
  AddressesApi,
  AssignmentAttachInput,
  AssignmentDetachInput,
  EntityMappingInput,
  PrimaryAddressPointer,
} from "@authe/fe-commons/addresses";
import type { FeatureFlags } from "@authe/fe-commons/flags";
import type { GraphQLClient } from "@authe/fe-commons/graphql";
import {
  GraphQLRequestError,
  graphqlAttachAssignment,
  graphqlClearPrimary,
  graphqlDeleteEntityMapping,
  graphqlDetachAssignment,
  graphqlFetchAddressDetail,
  graphqlGetEntityMapping,
  graphqlListAssignments,
  graphqlPutEntityMapping,
  graphqlSetPrimary,
  toAddressScope,
} from "@authe/fe-commons/graphql";
import { type DataOperation, resolveTransport } from "@authe/fe-commons/transport";
import { createLogger } from "@authe/fe-commons/logging";

const log = createLogger("dashboard:graphql-addresses");

export interface TransportAwareAddressesOptions {
  /** The GraphQL client, or `null` when unconfigured (always REST). */
  graphqlClient: GraphQLClient | null;
  /** The live feature-flag set (registry precedence: master + per-op). */
  flags: FeatureFlags;
}

/**
 * Run `op` over GraphQL when the registry routes it there, else (or on any
 * GraphQL transport error) fall back to the REST call. A non-GraphQL error from
 * the GraphQL path is re-thrown (a programming error must not be masked). The
 * fallback runs the REST closure so a flipped flag can never cause an outage.
 */
async function route<T>(
  operation: DataOperation,
  graphqlClient: GraphQLClient | null,
  flags: FeatureFlags,
  runGraphql: (client: GraphQLClient) => Promise<T>,
  runRest: () => Promise<T>,
): Promise<T> {
  const wantsGraphql =
    graphqlClient != null &&
    resolveTransport(operation, flags, { graphqlConfigured: true }) === "graphql";
  if (wantsGraphql && graphqlClient) {
    try {
      return await runGraphql(graphqlClient);
    } catch (err) {
      if (!(err instanceof GraphQLRequestError)) throw err;
      log.warn(`${operation} GraphQL failed, falling back to REST`, {
        status: err.status,
        message: err.message,
      });
    }
  }
  return runRest();
}

export function withGraphqlAddresses(
  rest: AddressesApi,
  { graphqlClient, flags }: TransportAwareAddressesOptions,
): AddressesApi {
  return {
    ...rest,

    async getDetail(chain, address, opts, signal) {
      return route(
        "address.detail",
        graphqlClient,
        flags,
        async (client) => {
          const detail = await graphqlFetchAddressDetail(
            client,
            {
              address,
              chainId: chain,
              withScopes: opts?.withScopes ?? false,
              // The widen is derived from the picked scope's tier name (`tenant`
              // / `org` / …) → the SDL `AddressScope` enum; a non-widenable tier
              // (e.g. `team`) maps to `undefined`, i.e. the caller's own view.
              scope: opts?.scope ? toAddressScope(opts.scope.scope) : undefined,
            },
            { signal },
          );
          // The resolver never 404s (it synthesizes an unsaved row), so `null`
          // is effectively unreachable; fall back to REST if it ever happens.
          return detail ?? rest.getDetail(chain, address, opts, signal);
        },
        () => rest.getDetail(chain, address, opts, signal),
      );
    },

    async listAssignments(chain: string, address: string, signal?: AbortSignal): Promise<AddressAssignment[]> {
      return route(
        "address.assignments.list",
        graphqlClient,
        flags,
        (client) => graphqlListAssignments(client, { address, chainId: chain }, { signal }),
        () => rest.listAssignments(chain, address, signal),
      );
    },

    async attachAssignment(
      chain: string,
      address: string,
      input: AssignmentAttachInput,
    ): Promise<AddressAssignment> {
      return route(
        "address.assignments.write",
        graphqlClient,
        flags,
        (client) => graphqlAttachAssignment(client, { address, chainId: chain, input }),
        () => rest.attachAssignment(chain, address, input),
      );
    },

    async detachAssignment(chain: string, address: string, input: AssignmentDetachInput): Promise<void> {
      return route(
        "address.assignments.write",
        graphqlClient,
        flags,
        (client) => graphqlDetachAssignment(client, { address, chainId: chain, input }),
        () => rest.detachAssignment(chain, address, input),
      );
    },

    async setPrimary(chain: string, address: string, entityId: string): Promise<PrimaryAddressPointer> {
      return route(
        "address.primary.write",
        graphqlClient,
        flags,
        (client) => graphqlSetPrimary(client, { address, chainId: chain, entityId }),
        () => rest.setPrimary(chain, address, entityId),
      );
    },

    async clearPrimary(chain: string, address: string, entityId: string): Promise<void> {
      return route(
        "address.primary.write",
        graphqlClient,
        flags,
        (client) => graphqlClearPrimary(client, { chainId: chain, entityId }),
        () => rest.clearPrimary(chain, address, entityId),
      );
    },

    async getEntityMapping(
      chain: string,
      address: string,
      signal?: AbortSignal,
    ): Promise<AddressEntityMapping | null> {
      return route(
        "address.entityMapping.read",
        graphqlClient,
        flags,
        (client) => graphqlGetEntityMapping(client, { address, chainId: chain }, { signal }),
        () => rest.getEntityMapping(chain, address, signal),
      );
    },

    async putEntityMapping(
      chain: string,
      address: string,
      input: EntityMappingInput,
    ): Promise<AddressEntityMapping> {
      return route(
        "address.entityMapping.write",
        graphqlClient,
        flags,
        (client) => graphqlPutEntityMapping(client, { address, chainId: chain, input }),
        () => rest.putEntityMapping(chain, address, input),
      );
    },

    async deleteEntityMapping(chain: string, address: string): Promise<void> {
      await route(
        "address.entityMapping.write",
        graphqlClient,
        flags,
        (client) => graphqlDeleteEntityMapping(client, { address, chainId: chain }),
        async () => {
          await rest.deleteEntityMapping(chain, address);
          return 0;
        },
      );
    },
  };
}
